import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, CheckCircle2, AlertCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { useAdminAuth } from '../../hooks/useAdminAuth';
import { useFeeSchedulesWithItems, useCompanyFeeSchedule } from '../../hooks/useFeeSchedules';
import { supabase } from '../../lib/supabase';
import { calculatePreview, formatCents } from '../../utils/feePreview';

interface CompanyResult {
  id: string;
  name: string;
}

export default function AdminFeeSchedulesPage() {
  const navigate = useNavigate();
  const { adminUser, isLoading: authLoading } = useAdminAuth();
  const { data: schedules, isLoading: isLoadingSchedules } = useFeeSchedulesWithItems();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<CompanyResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<CompanyResult | null>(null);
  const [selectedScheduleId, setSelectedScheduleId] = useState<string>('');
  const [previewAmount, setPreviewAmount] = useState(10000); // cents, default $100
  const [isSaving, setIsSaving] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const {
    data: companySchedule,
    isLoading: isLoadingCompanySchedule,
    refetch: refetchCompanySchedule,
  } = useCompanyFeeSchedule(selectedCompany?.id ?? null);

  // Set dropdown to current schedule when company schedule loads
  useEffect(() => {
    if (companySchedule) {
      setSelectedScheduleId(companySchedule.schedule_id);
    }
  }, [companySchedule]);

  // Clear notification after a delay
  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleSearch = useCallback(async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name')
        .ilike('name', `%${searchTerm}%`)
        .limit(20);

      if (error) {
        console.error('[AdminFeeSchedules] Search error:', error);
        setSearchResults([]);
      } else {
        setSearchResults((data as CompanyResult[]) || []);
      }
    } catch (err) {
      console.error('[AdminFeeSchedules] Search error:', err);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [searchTerm]);

  // Debounced search
  useEffect(() => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(() => {
      handleSearch();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, handleSearch]);

  const handleSelectCompany = (company: CompanyResult) => {
    setSelectedCompany(company);
    setSearchTerm('');
    setSearchResults([]);
  };

  const handleAssign = async () => {
    if (!selectedCompany || !selectedScheduleId) return;

    setIsSaving(true);
    try {
      const { data, error } = await supabase
        .from('subscription_billing_settings')
        .update({ fee_schedule_id: selectedScheduleId })
        .eq('company_id', selectedCompany.id)
        .select('company_id')
        .single();

      if (error) {
        const isNoRow = error.code === 'PGRST116';
        const message = isNoRow
          ? `No billing settings found for ${selectedCompany.name}. Set up billing first.`
          : `Failed to assign schedule: ${error.message}`;
        console.error('[AdminFeeSchedules] Assignment error:', error);
        setNotification({ type: 'error', message });
      } else {
        setNotification({ type: 'success', message: `Fee schedule updated for ${selectedCompany.name}.` });
        refetchCompanySchedule();
      }
    } catch (err) {
      console.error('[AdminFeeSchedules] Assignment error:', err);
      setNotification({ type: 'error', message: 'An unexpected error occurred.' });
    } finally {
      setIsSaving(false);
    }
  };

  // Find the selected schedule from the loaded schedules (with items) for reactive preview
  const selectedSchedule = schedules?.find(s => s.id === selectedScheduleId);
  const previewItems = selectedSchedule?.items ?? companySchedule?.items;
  const preview = previewItems ? calculatePreview(previewItems, previewAmount) : null;

  if (authLoading || isLoadingSchedules) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1421]" />
      </div>
    );
  }

  if (!adminUser) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-[#A9A3A3]">Unauthorized. Admin access required.</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto font-['Inter']">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-[#EBFFEE]">Fee Schedule Management</h1>
        <p className="text-[#A9A3A3] mt-1">View active fee schedules and manage company assignments.</p>
      </div>

      {/* Notification */}
      {notification && (
        <div
          className={`mb-6 p-4 rounded-md flex items-center gap-2 ${
            notification.type === 'success'
              ? 'bg-green-900/30 border border-green-700 text-green-300'
              : 'bg-red-900/30 border border-red-700 text-red-300'
          }`}
        >
          {notification.type === 'success' ? (
            <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
          ) : (
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
          )}
          <span className="text-sm">{notification.message}</span>
        </div>
      )}

      {/* Active Schedules */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#EBFFEE] mb-4">Active Schedules</h2>
        {schedules && schedules.length > 0 ? (
          <>
            {/* Customer Subscription Schedules */}
            {schedules.some(s => s.context === 'customer_subscription') && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-[#A9A3A3] uppercase tracking-wider mb-3">
                  Customer Subscription
                </h3>
                <div className="flex flex-wrap gap-4">
                  {schedules
                    .filter(s => s.context === 'customer_subscription')
                    .map((schedule) => (
                      <Card
                        key={schedule.id}
                        className="bg-[#221F1F] border-[#504A4A] w-64 cursor-pointer hover:border-[#9B1421] transition-colors"
                        onClick={() => navigate(`/admin/fee-schedules/${schedule.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-[#EBFFEE] font-semibold">{schedule.name}</h3>
                            {schedule.is_default && (
                              <span className="px-2 py-0.5 rounded text-xs bg-[#9B1421] text-white">
                                Default
                              </span>
                            )}
                          </div>
                          <span className="inline-block px-2 py-0.5 rounded text-xs bg-[#363232] text-[#A9A3A3] mb-1">
                            Customer
                          </span>
                          {schedule.description && (
                            <p className="text-[#A9A3A3] text-sm">{schedule.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            )}

            {/* SaaS Subscription Schedules */}
            {schedules.some(s => s.context === 'saas_subscription') && (
              <div className="mb-6">
                <h3 className="text-sm font-medium text-[#A9A3A3] uppercase tracking-wider mb-3">
                  SaaS Subscription
                </h3>
                <div className="flex flex-wrap gap-4">
                  {schedules
                    .filter(s => s.context === 'saas_subscription')
                    .map((schedule) => (
                      <Card
                        key={schedule.id}
                        className="bg-[#221F1F] border-[#504A4A] w-64 cursor-pointer hover:border-[#9B1421] transition-colors"
                        onClick={() => navigate(`/admin/fee-schedules/${schedule.id}`)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-[#EBFFEE] font-semibold">{schedule.name}</h3>
                            {schedule.is_default && (
                              <span className="px-2 py-0.5 rounded text-xs bg-[#9B1421] text-white">
                                Default
                              </span>
                            )}
                          </div>
                          <span className="inline-block px-2 py-0.5 rounded text-xs bg-[#363232] text-[#A9A3A3] mb-1">
                            SaaS
                          </span>
                          {schedule.description && (
                            <p className="text-[#A9A3A3] text-sm">{schedule.description}</p>
                          )}
                        </CardContent>
                      </Card>
                    ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-[#A9A3A3] text-sm">No active fee schedules found.</p>
        )}
      </div>

      {/* Company Search + Assignment */}
      <div className="mb-8">
        <h2 className="text-lg font-semibold text-[#EBFFEE] mb-4">Company Assignment</h2>

        {/* Search */}
        <div className="relative mb-4 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A9A3A3]" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search company name..."
              className="w-full pl-10 pr-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] placeholder-[#A9A3A3] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
            />
          </div>

          {/* Search results dropdown */}
          {searchResults.length > 0 && (
            <div className="absolute z-10 w-full mt-1 bg-[#221F1F] border border-[#504A4A] rounded-md shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleSelectCompany(company)}
                  className="w-full text-left px-4 py-2 text-sm text-[#EBFFEE] hover:bg-[#363232] transition-colors"
                >
                  {company.name}
                </button>
              ))}
            </div>
          )}
          {isSearching && (
            <div className="absolute z-10 w-full mt-1 bg-[#221F1F] border border-[#504A4A] rounded-md p-3">
              <p className="text-[#A9A3A3] text-sm">Searching...</p>
            </div>
          )}
        </div>

        {/* Selected company assignment panel */}
        {selectedCompany && (
          <Card className="bg-[#221F1F] border-[#504A4A] max-w-2xl">
            <CardHeader>
              <CardTitle className="text-[#EBFFEE] text-base">
                {selectedCompany.name}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoadingCompanySchedule ? (
                <p className="text-[#A9A3A3] text-sm">Loading current schedule...</p>
              ) : (
                <>
                  {/* Current schedule */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                      Current Schedule
                    </label>
                    <p className="text-[#A9A3A3] text-sm">
                      {companySchedule ? companySchedule.schedule_name : 'None assigned (will use default)'}
                    </p>
                  </div>

                  {/* Assignment dropdown */}
                  <div>
                    <label className="block text-sm font-medium text-[#EBFFEE] mb-1">
                      Assign Schedule
                    </label>
                    <div className="flex gap-2">
                      <select
                        value={selectedScheduleId}
                        onChange={(e) => setSelectedScheduleId(e.target.value)}
                        className="flex-1 px-3 py-2 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                      >
                        <option value="">Select a schedule...</option>
                        {schedules
                          ?.filter((s) => s.context === 'customer_subscription')
                          .map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}{s.is_default ? ' (Default)' : ''}
                          </option>
                        ))}
                      </select>
                      <Button
                        onClick={handleAssign}
                        disabled={!selectedScheduleId || isSaving}
                        className="bg-[#9B1421] hover:bg-[#7D101A] text-white px-4 py-2 rounded-md disabled:opacity-50"
                      >
                        {isSaving ? 'Saving...' : 'Save'}
                      </Button>
                    </div>
                  </div>

                  {/* Fee Preview */}
                  {previewItems && (
                    <div>
                      <label className="block text-sm font-medium text-[#EBFFEE] mb-2">
                        Fee Preview
                      </label>
                      <div className="mb-3">
                        <label className="block text-xs text-[#A9A3A3] mb-1">
                          Subscription amount ($)
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={previewAmount / 100}
                          onChange={(e) => setPreviewAmount(Math.round(parseFloat(e.target.value || '0') * 100))}
                          className="w-32 px-3 py-1.5 bg-[#363232] border border-[#504A4A] rounded-md text-[#EBFFEE] text-sm focus:outline-none focus:ring-2 focus:ring-[#9B1421]"
                        />
                      </div>
                      {preview && (
                        <div className="space-y-1.5">
                          <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                            <span className="text-[#A9A3A3] text-sm">Base subscription</span>
                            <span className="text-[#EBFFEE] text-sm">{formatCents(previewAmount)}</span>
                          </div>
                          {preview.lineItems
                            .filter(li => li.chargeEffect === 'add' && li.amountCents > 0)
                            .map((li, idx) => (
                            <div key={idx} className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                              <span className="text-[#A9A3A3] text-sm">{li.heading}</span>
                              <span className="text-[#EBFFEE] text-sm">+{formatCents(li.amountCents)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between items-center px-3 py-2 bg-[#504A4A] rounded-md font-semibold">
                            <span className="text-[#EBFFEE] text-sm">Student pays</span>
                            <span className="text-[#EBFFEE] text-sm">{formatCents(preview.payerCents)}</span>
                          </div>
                          <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                            <span className="text-[#A9A3A3] text-sm">Gym receives</span>
                            <span className="text-green-400 text-sm">{formatCents(preview.gymReceives)}</span>
                          </div>
                          <div className="flex justify-between items-center px-3 py-1.5 bg-[#363232] rounded-md">
                            <span className="text-[#A9A3A3] text-sm">Platform fee</span>
                            <span className="text-[#EBFFEE] text-sm">{formatCents(preview.platformFee)}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
